import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { getCurrency } from '../../../utils';
import Image from '../../../components/ContentfulImage';
import Placeholder from '../../../components/Placeholder';
import Slider from '../../../components/Slider/Slider';

import {
  Wrapper,
  SliderWrapper,
  Description,
  TextWrapper,
  VariantSelect,
  VariantSelectWrapper,
  TaxNote,
  DesktopSelector,
  MobileSelector,
  ImageContainer,
} from './ProductHeroOverview.style';

const getVariantLabel = (volume, unit, price, locale) => {
  let label = `${volume}${unit}`;

  if (price) {
    const localeName = locale.split('_')[0];
    const currencyName = getCurrency(locale);
    label = `${label} ${new Intl.NumberFormat(localeName, { style: 'currency', currency: currencyName }).format(
      price
    )}`;
  }
  return label;
};

const Selector = ({ copy, locale, onQuantitySelect, currentVariant, product }) => {
  let targetItem = null;
  if (product.variants && product.variants.length > 0 && currentVariant >= 0) {
    targetItem = _.find(product.variants, item => _.get(item, 'volume') === currentVariant);
  }

  return (
    <VariantSelectWrapper>
      {product.variants &&
      product.variants.length > 0 &&
      product.variants[0].volume != null &&
      product.variants[0].unit != null ? (
        <VariantSelect>
          <p>{copy.sizes || <Placeholder w="50px" h="25px" />}:</p>
          <select name="product-variants" onChange={onQuantitySelect} value={currentVariant}>
            {product.variants &&
              product.variants.length > 0 &&
              product.variants.map(({ entryId, volume, unit, price, salonExclusive }) => (
                <option key={entryId} value={volume}>
                  {`${getVariantLabel(volume, unit, price, locale)}`}
                  {!!(price && !salonExclusive) && _.get(copy, 'tax') && ` ${copy.tax}`}
                </option>
              ))}
          </select>
        </VariantSelect>
      ) : null}
      {!!(targetItem && targetItem.price && !targetItem.salonExclusive && _.get(copy, 'taxnote')) && (
        <TaxNote>{copy.taxnote}</TaxNote>
      )}
    </VariantSelectWrapper>
  );
};

Selector.propTypes = {
  copy: PropTypes.shape().isRequired,
  locale: PropTypes.string.isRequired,
  onQuantitySelect: PropTypes.func.isRequired,
  currentVariant: PropTypes.number.isRequired,
  product: PropTypes.shape({}).isRequired,
};

class ProductHeroOverview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentVariant: this.props.product.variants ? this.props.product.variants[0].volume : 0,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.product.name !== this.props.product.name) {
      setTimeout(() => {
        this.setState({
          currentVariant: this.props.product.variants ? this.props.product.variants[0].volume : 0,
        });
      }, 200);
    }
  }

  handleQuantitySelect = e => {
    const selectedVariant = e.target.value;

    this.setState({
      currentVariant: selectedVariant,
    });
  };

  handleSlideChange = i => {
    const val = this.props.product.variants.find((variant, variantIndex) => variantIndex === i);
    this.setState({
      currentVariant:
        (val && val.volume) || this.props.product.variants.find((variant, variantIndex) => variantIndex === 0).volume,
    });
    // this.setState({
    //   currentVariant: this.props.product.variants.find((variant, variantIndex) => variantIndex === i).volume,
    // });
  };

  render() {
    const { copy, locale, product } = this.props;
    const { currentVariant } = this.state;

    return (
      <Wrapper>
        <SliderWrapper>
          {product.variants ? (
            <Slider
              currentSlide={product.variants.map(({ volume }) => volume).indexOf(parseInt(currentVariant, 10))}
              dots
              keys={product.variants.map(({ entryId }) => entryId)}
              options={{
                duration: 400,
                easing: 'ease-in-out',
                loop: true,
                onChange: this.handleSlideChange,
              }}
            >
              {product.variants &&
                product.variants.length > 0 &&
                product.variants.map(({ entryId, image }) => (
                  <ImageContainer key={entryId}>
                    {image ? (
                      <Image src={image} options={{ size: [500], w: '100%' }} />
                    ) : (
                      <Placeholder w="100%" pb="100%" />
                    )}
                  </ImageContainer>
                ))}
            </Slider>
          ) : (
            <Placeholder w="100%" pb="100%" />
          )}
        </SliderWrapper>

        <Description>
          <TextWrapper>
            <h5>{(product.range && product.range.name) || <Placeholder w="150px" h="21px" />}</h5>
            <h2>{product.name || <Placeholder w="80%" h="90px" />}</h2>
            <p>{product.description || <Placeholder w="100%" h="110px" />}</p>
          </TextWrapper>
          <DesktopSelector>
            <Selector
              onQuantitySelect={this.handleQuantitySelect}
              currentVariant={parseInt(currentVariant, 10)}
              product={product}
              copy={copy}
              locale={locale}
            />
          </DesktopSelector>
        </Description>

        <MobileSelector>
          <Selector
            onQuantitySelect={this.handleQuantitySelect}
            currentVariant={parseInt(currentVariant, 10)}
            product={product}
            copy={copy}
            locale={locale}
          />
        </MobileSelector>
      </Wrapper>
    );
  }
}

ProductHeroOverview.defaultProps = {
  product: {},
  screenSize: null,
};

ProductHeroOverview.propTypes = {
  copy: PropTypes.shape().isRequired,
  locale: PropTypes.string.isRequired,
  product: PropTypes.shape({
    slug: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        salonExclusive: PropTypes.bool,
        volume: PropTypes.number,
        unit: PropTypes.string,
        image: PropTypes.shape({
          title: PropTypes.string,
          description: PropTypes.string,
          file: PropTypes.shape({
            url: PropTypes.string,
            details: PropTypes.shape({
              size: PropTypes.number,
              image: PropTypes.shape({
                width: PropTypes.number,
                height: PropTypes.number,
              }),
            }),
            fileName: PropTypes.string,
          }),
        }),
      })
    ),
    range: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    }),
    series: PropTypes.shape({
      name: PropTypes.string,
    }),
    benefits: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    keyIngredients: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    otherIngredients: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    image: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        file: PropTypes.shape({
          url: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      })
    ),
  }),
};

export default ProductHeroOverview;



// WEBPACK FOOTER //
// ./src/pages/ProductPage/ProductHeroOverview/ProductHeroOverview.js